import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// ─── Module-level singleton ────────────────────────────────────────────────────
// beforeinstallprompt fires ONCE per page load. Using a singleton ensures all
// hook instances (Header, Navbar, PwaInstallPrompt, LandingPage) share the same
// deferred prompt rather than competing for the event.
let _prompt: BeforeInstallPromptEvent | null = null;
let _installed = false;
let _pendingInstall = false;
const _subscribers = new Set<() => void>();

function notify() { _subscribers.forEach(fn => fn()); }

if (typeof window !== 'undefined') {
  // Already installed?
  if (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  ) {
    _installed = true;
  }

  window.addEventListener('beforeinstallprompt', async (e) => {
    e.preventDefault();
    const prompt = e as BeforeInstallPromptEvent;

    if (_pendingInstall) {
      // User already clicked Install before the event arrived
      _pendingInstall = false;
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') { _installed = true; _prompt = null; }
    } else {
      _prompt = prompt;
    }
    notify();
  });

  window.addEventListener('appinstalled', () => {
    _installed = true;
    _prompt = null;
    notify();
  });
}

function isIOS(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export interface PwaInstallState {
  canInstall: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  install: () => Promise<void>;
}

export function usePwaInstall(): PwaInstallState {
  const [, rerender] = useState(0);
  const ios = isIOS();

  useEffect(() => {
    const sub = () => rerender(n => n + 1);
    _subscribers.add(sub);
    return () => { _subscribers.delete(sub); };
  }, []);

  const install = async () => {
    if (ios) return;

    if (_prompt) {
      await _prompt.prompt();
      const { outcome } = await _prompt.userChoice;
      if (outcome === 'accepted') {
        _installed = true;
        _prompt = null;
        notify();
      }
    } else {
      // Event hasn't arrived yet — set pending flag; handler above will fire prompt
      _pendingInstall = true;
    }
  };

  return {
    canInstall: !_installed,
    isInstalled: _installed,
    isIOS: ios,
    install,
  };
}